// import node module libraries
import { useEffect, useState } from 'react';

// import node module libraries
import { Link } from 'react-router-dom';
import { Form, Button, Col, Row, Image, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// import authentication components
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// import custom components
import SectionHeadingLeft from 'components/marketing/common/section-headings/SectionHeadingLeft';

// import sub components
import BillingInformationForm from './BillingInformationForm';
import ModalCheckout from './ModalCheckout';

// import media files
import Logo from 'assets/images/brand/logo/logo-inverse.svg';

const Checkout = () => {
	const { t } = useTranslation();
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });

	const location = useLocation();
	const product = location.state?.plan;

	const [isButtonDisabled, setIsButtonDisabled] = useState(true);
	const [isLoading, setIsLoading] = useState(false); // State for loading spinner

	const authHeader = useAuthHeader();
	// fetch data from api
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const auth = useAuthUser(); // Get the authenticated user data

	const [CustomerData, setCustomerData] = useState([]);
	const [isLoadingCustomerData, setIsLoadingCustomerData] = useState(true);
	const getCustomer = async () => {
		setIsLoadingCustomerData(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/customer`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setCustomerData(responseData);
				setIsLoadingCustomerData(false);
			})
			.catch(() => {
				setIsLoadingCustomerData(false);
			});
	}

	const [isLoadingUpdateCustomerData, setIsLoadingUpdateCustomerData] = useState(false);
	const updateCustomer = async (billingData) => {
		setIsLoadingUpdateCustomerData(true);

		const customerID = CustomerData.id;
		const body = {
			customer_name: billingData.company,
			customer_email: billingData.email,
			customer_address: {
				customer_address_line1: billingData.line1,
				customer_address_line2: billingData.line2,
				customer_address_city: billingData.city,
				customer_address_postal_code: billingData.postal_code,
				customer_address_country: billingData.country
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/tenants/${auth.tenant_id}/customer/${customerID}`, body, options)
			.then(response => {
				const responseData = response.data.result; // Extract the data from the response
				setCustomerData(responseData);
				setIsLoadingUpdateCustomerData(false);
			})
			.catch(() => {
				setIsLoadingUpdateCustomerData(false);
			});
	}

	const [CustomerTaxData, setCustomerTaxData] = useState([]);
	const [isLoadingCreateCustomerTaxData, setIsLoadingCreateCustomerTaxData] = useState(false);
	const createCustomerTax = async (taxData) => {
		setIsLoadingCreateCustomerTaxData(true);

		const body = {
			customer_tax_id_type: taxData.tax_id_type,
			customer_tax_id_value: taxData.tax_id_value
		};
		
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/tenants/${auth.tenant_id}/customer/tax_ids`, body, options)
			.then(response => {
				const responseData = response.data.result; // Extract the data from the response
				if (responseData && responseData.id) {
					// Erfolgreich erstellt, jetzt neue Tax-Daten abrufen
					getCustomerTax();
				}
				setIsLoadingCreateCustomerTaxData(false);
			})
			.catch(() => {
				setIsLoadingCreateCustomerTaxData(false);
			});
	}

	const [isLoadingCustomerTaxData, setIsLoadingCustomerTaxData] = useState(true);
	const getCustomerTax = async () => {
		setIsLoadingCustomerTaxData(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/customer/tax_ids`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setCustomerTaxData(responseData);
				setIsLoadingCustomerTaxData(false);
			})
			.catch(() => {
				setIsLoadingCustomerTaxData(false);
			});
	}

	const [isLoadingDeleteCustomerTaxData, setIsLoadingDeleteCustomerTaxData] = useState(false);
	const deleteCustomerTax = async (taxData) => {
		setIsLoadingDeleteCustomerTaxData(true);

		// Überprüfen, ob taxData ein Array ist und mindestens ein Element mit ID enthält
		if (!Array.isArray(taxData) || taxData.length === 0 || !taxData[0].id) {
			setIsLoadingDeleteCustomerTaxData(false);
			return;
		}

		// Extrahieren der ID des ersten Elements
		const taxID = taxData[0].id;

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		try {
			const response = await axios.delete(`${baseURL}/tenants/${auth.tenant_id}/customer/tax_ids/${taxID}`, options);
			const responseData = response.data.result;

			if (responseData && responseData.id === taxID && responseData.deleted === true) {
				// Erfolgreich gelöscht, jetzt neue Tax-Daten erstellen
				createCustomerTax(taxData[0]);
			}
			setIsLoadingDeleteCustomerTaxData(false);
		} catch (error) {
			console.error('Fehler beim Löschen der Tax-Daten:', error);
			setIsLoadingDeleteCustomerTaxData(false);
		}
	};

	const deleteCustomerTaxOnly = async (taxData) => {
		setIsLoadingDeleteCustomerTaxData(true);

		// Überprüfen, ob taxData ein Array ist und mindestens ein Element mit ID enthält
		if (!Array.isArray(taxData) || taxData.length === 0 || !taxData[0].id) {
			setIsLoadingDeleteCustomerTaxData(false);
			return;
		}

		// Extrahieren der ID des ersten Elements
		const taxID = taxData[0].id;

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		try {
			const response = await axios.delete(`${baseURL}/tenants/${auth.tenant_id}/customer/tax_ids/${taxID}`, options);
			const responseData = response.data.result;

			if (responseData && responseData.id === taxID && responseData.deleted === true) {
				// Erfolgreich gelöscht - Tax-Daten aus dem State entfernen
				getCustomerTax();
			}
			setIsLoadingDeleteCustomerTaxData(false);
		} catch (error) {
			console.error('Fehler beim Löschen der Tax-Daten:', error);
			setIsLoadingDeleteCustomerTaxData(false);
		}
	};

	const [CurrentSubscriptionsData, setCurrentSubscriptionsData] = useState([]);
	const [isLoadingCurrentSubscriptions, setIsLoadingCurrentSubscriptions] = useState(true);
	const getCurrentSubscriptions = async () => {
		setIsLoadingCurrentSubscriptions(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/subscriptions`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setCurrentSubscriptionsData(responseData);
				setIsLoadingCurrentSubscriptions(false);
			})
			.catch(() => {
				setIsLoadingCurrentSubscriptions(false);
			});
	}

	const [isLoadingCreateSubscriptions, setIsLoadingCreateSubscription] = useState(true);
	const createSubscription = async () => {
		setIsLoadingCreateSubscription(true);

		const body = {
			collection_method: 'send_invoice', // Rechnung senden
			days_until_due: 14, // Zahlungsziel von 14 Tagen
			items: [
				{
					price: product.id, // ID of the plan
					quantity: 1 // Adjust quantity as needed
				}
			],
			//trial_period_days: 7, // Trial period of 7 days
			metadata: {
				tenant_id: auth.tenant_id,
				tenant_slug: auth.tenant_slug,
			},
			currency: product.currency || 'eur', // Währung aus dem übergebenen Produkt, Standard ist EUR
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/tenants/${auth.tenant_id}/subscriptions`, body, options)
			.then(response => {
				const responseData = response.data.result; // Extract the data from the response
				console.log('Subscription created successfully:', responseData);
				setIsLoadingCreateSubscription(false);
			})
			.catch(() => {
				setIsLoadingCreateSubscription(false);
			});
	}

	const [isLoadingCancelSubscriptions, setIsLoadingCancelSubscription] = useState(true);
	const cancelSubscription = async () => {
		setIsLoadingCancelSubscription(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.delete(`${baseURL}/tenants/${auth.tenant_id}/subscriptions/${CurrentSubscriptionsData[0].id}`, options)
			.then(response => {
				const responseData = response.data.result; // Extract the data from the response
				console.log('Subscription canceled successfully:', responseData);
				setIsLoadingCancelSubscription(false);
			})
			.catch(() => {
				setIsLoadingCancelSubscription(false);
			});
	}

	// Funktion zum einfachen Vergleich
	const getSimpleComparison = () => {
		if (!CurrentSubscriptionsData || CurrentSubscriptionsData.length === 0 || !product) {
			return null;
		}

		const currentPlan = CurrentSubscriptionsData[0];

		return {
			currentPlanName: currentPlan.product.name || t('No current Plan'),
			currentPrice: ((currentPlan.plan.amount || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 0 }),
			currentInterval: currentPlan.interval,
			currentBillingPeriod: new Date(currentPlan.items.data[0].current_period_end * 1000).toLocaleDateString() || t('No billing period'),
			currentTrial: currentPlan.trial_end ? new Date(currentPlan.trial_end * 1000).toLocaleDateString() : null,
			newPlanName: product.product_name,
			newPrice: (product.amount / 100).toLocaleString('de-DE', { minimumFractionDigits: 0 }),
			newInterval: product.interval
		};
	};
	const comparison = getSimpleComparison();

	// Funktion zur Validierung der Rechnungsinformationen
	const validateBillingInformation = () => {
		if (!CustomerData || Object.keys(CustomerData).length === 0) {
			setIsButtonDisabled(true);
			return false;
		}
		const requiredFields = [
			CustomerData.address?.line1,
			CustomerData.address?.country,
			CustomerData.address?.postal_code,
			CustomerData.address?.city,
			CustomerData.name,
			CustomerData.email
		];
		const allFieldsSet = requiredFields.every(field => field && field.toString().trim() !== '');
		//setIsButtonDisabled(!allFieldsSet);
		return allFieldsSet;
	};

	// State für Validierungsstatus
	const [isBillingInfoComplete, setIsBillingInfoComplete] = useState(false);

	// Funktionen für Billing Modal
	const handleShowBillingModal = () => setShowBillingModal(true);
	const handleCloseBillingModal = () => setShowBillingModal(false);
	const [showBillingModal, setShowBillingModal] = useState(false); // State für Modal

	// Funktionen für Checkout Modal
	const handleShowCheckoutModal = () => setShowCheckoutModal(true);
	const handleCloseCheckoutModal = () => setShowCheckoutModal(false);
	const [showCheckoutModal, setShowCheckoutModal] = useState(false); // State für Modal
	const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

	// Callback-Funktion für erfolgreiches Updates
	const handleBillingInfoUpdate = (billingData, taxData) => {
		updateCustomer(billingData); // Kundendaten aktualisieren
		// Taxdaten verarbeiten
		if (taxData && taxData.length > 0) {
			const taxItem = taxData[0];

			if (taxItem.id && taxItem.id !== '') {
				// Bestehende Tax-ID vorhanden
				if (taxItem.tax_id_type && taxItem.tax_id_value) {
					// Neue Daten vorhanden -> löschen und neu erstellen
					deleteCustomerTax(taxData);
				} else {
					// Keine neuen Daten -> nur löschen
					deleteCustomerTaxOnly(taxData);
				}
			} else {
				// Keine bestehende ID -> nur erstellen (falls Daten vorhanden)
				if (taxItem.tax_id_type && taxItem.tax_id_value) {
					createCustomerTax(taxItem);
				}
			}
		}
		handleCloseBillingModal(); // Modal schließen
	};

	// Funktion für Button-Click
	const [showConfetti, setShowConfetti] = useState(false);
	const handleAddWithCosts = () => {
		setShowCheckoutModal(true);
		setIsLoadingCheckout(true);
		if (CurrentSubscriptionsData.length === 0) {
			createSubscription().then(() => {
				setIsLoadingCheckout(false);
				setShowConfetti(true);
			});
		} else {
			// Hole Zeitraum für aktuelle Subscription
			const currentSubscription = CurrentSubscriptionsData[0];
			const currentPeriodEnd = currentSubscription.current_period_end;
			
			cancelSubscription().then(() => {
				createSubscription().then(() => {
					setIsLoadingCheckout(false);
					setShowConfetti(true);
				});
			});
		}
	};
	
	useEffect(() => {
		getCustomer();
		getCustomerTax();
		getCurrentSubscriptions();
	}, []);

	useEffect(() => {
		if (!isLoadingCustomerData && !isLoadingCustomerTaxData) {
			setIsBillingInfoComplete(validateBillingInformation());
		}
	}, [CustomerData, isLoadingCustomerData, CustomerTaxData, isLoadingCustomerTaxData]);

	return (
		<main>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<section className="container-fluid bg-primary">
				<Row className="align-items-center min-vh-100">
					<Col lg={6} md={12} sm={12}>
						<div className="px-xl-20 px-md-8 px-4 py-8 py-lg-0">
							{/* Checkout Summary */}
							<Link to="/marketing/pages/compare-plan" className="text-white">
								<i className="fe fe-arrow-left"></i> {t('Back to Compare Plans')}
							</Link>
							<div className="d-flex justify-content-between my-4 align-items-center">
								<Link to="/">
									<Image src={Logo} width={100} alt="haeppy.app Logo" />
								</Link>
							</div>
							<div className="text-white">
								<h1 className="display-4 text-white fw-bold">{product.product_name || t('Product')}</h1>
								<div className="mb-2">
									<span className="display-6 text-white">
										{(product.amount / 100).toLocaleString('de-DE', { minimumFractionDigits: 0 })} €
									</span>
									<span className="ms-2 text-light-primary">
										/{product.interval === 'year'
											? t('year')
											: product.interval === 'month'
												? t('month')
												: ''}
									</span>
								</div>
								<div className="mt-3">
									<p className="fs-4 mb-4">
										{product.product_description}
									</p>
									{/* Features */}
									{product.marketing_features && (
										<ul className="list-unstyled mt-3 ms-2">
											{product.marketing_features.map((feature, idx) => (
												<li key={idx} className="mb-1">
													<i className="fe fe-arrow-right"></i> {feature.name}
												</li>
											))}
										</ul>
									)}
								</div>
							</div>
						</div>
						{/* Imprint and Terms of Use links at the bottom */}
						<div className="text-center mt-5 pb-3 w-50 position-absolute bottom-0 start-0 d-none d-lg-block">
							<Link to="/marketing/about/imprint" className="text-light-primary me-3" target='_blank'>
								<small>{t('Imprint')} <i className="fe fe-arrow-up-right"></i></small>
							</Link>
							<Link to="/marketing/about/privacy" className="text-light-primary me-3" target='_blank'>
								<small>{t('Privacy')} <i className="fe fe-arrow-up-right"></i></small>
							</Link>
							<Link to="/marketing/about/terms-and-conditions" className="text-light-primary" target='_blank'>
								<small>{t('Terms and Conditions')} <i className="fe fe-arrow-up-right"></i></small>
							</Link>
						</div>
					</Col>

					{/* right side form section with background color */}
					<Col
						lg={6}
						className="d-lg-flex align-items-center w-lg-50 min-vh-lg-100 position-fixed-lg bg-cover bg-light top-0 right-0 px-xl-20 px-md-8 px-4 py-8 py-lg-0"
					>
						{/* Checkout Form Section */}
						<div className="w-100">
							<h2 className="mb-2">{t('Excellent choice - Get started right away!')}</h2>
							{isLoadingCurrentSubscriptions && (
								<>
									<Skeleton className="rounded" height="30px" count={1} />
									<Row className="my-2">
										<Skeleton className="rounded" height="30px" count={1} />
										<Row>
											<Skeleton className="rounded mt-3" height="60px" count={3} />
										</Row>
									</Row>
								</>
							)}
							{/* User has no current Plan */}
							{product && !isLoadingCurrentSubscriptions && CurrentSubscriptionsData.length === 0 && (
								<>
									<div className="mb-4">
										<p className="mb-4">{t('Nice to welcome you again. We are very happy that you are back.')} 💜</p>
									</div>
									<Row className="my-8">
										<h3>{t('Summary')}</h3><hr></hr>
										<Col xl={12} lg={12} className="align-items-center ms-3">
											{/* Plan Name */}
											<div className="mb-2 fs-4">
												<h5 className="mt-0">
													{t('Plan')}
												</h5>
												<i className="fe fe-arrow-right"></i> 
												<span className="text">
													{product.product_name || t('Plan name not available')}
												</span>
											</div>
										</Col>
										<Col xl={12} lg={12} className="align-items-center ms-3">
											{/* Price */}
											<div className="mb-2 fs-4">
												<h5 className="mt-0">
													{t('Price')}
												</h5>
													<i className="fe fe-arrow-right"></i> 
												<span className="">
													{(product.amount / 100 || 0).toLocaleString('de-DE', { minimumFractionDigits: 0 })} €
												</span>
											</div>
										</Col>
										<Col xl={12} lg={12} className="align-items-center ms-3">
											{/* Billing Interval */}
											<div className="mb-0 fs-4">
												<h5 className="mt-0">
													{t('Billing interval')}
												</h5>
												<i className="fe fe-arrow-right"></i>
												<span className="">
													{product.interval === 'year' ? t('Yearly') : t('Monthly')}
												</span>
											</div>
										</Col>
									</Row>
								</>
							)}
							{/* User has current Plan */}
							{comparison && !isLoadingCurrentSubscriptions && (
								<>
									<div className="mb-4">
										<p className=" mb-4">{t('We compare your previous plan with your selection and show you the most important differences.')}</p>
									</div>					
									<Row className="mb-6">
										<h3>{t('Summary')}</h3><hr></hr>
										<Col xl={12} lg={12} className="align-items-center ms-3">
											{/* Plan Name */}
											<div className="mb-2 fs-4">
												<h5 className="mt-0">
													{t('Plan')}
												</h5>
												<span className="text">
													{comparison.currentPlanName} 
												</span>
												<i className="fe fe-arrow-right"></i> 
												<span className="text-strong">
													{' '}{comparison.newPlanName}
												</span>
											</div>
										</Col>
										<Col xl={12} lg={12} className="align-items-center ms-3">
											{/* Price */}
											<div className="mb-2 fs-4">
												<h5 className="mt-0">
													{t('Price')}
												</h5>
												<span className="text">
													{comparison.currentPrice} €
												</span>
													<i className="fe fe-arrow-right"></i> 
												<span className="">
													{' '}{comparison.newPrice} €
												</span>
											</div>
										</Col>
										<Col xl={12} lg={12} className="align-items-center ms-3">
											{/* Billing Interval */}
											<div className="mb-0 fs-4">
												<h5 className="mt-0">
													{t('Billing interval')}
												</h5>
												<span className="text">
													{comparison.currentInterval === 'year' ? t('Yearly') : t('Monthly')} 
												</span>
												<i className="fe fe-arrow-right"></i>
												<span className="">
													{' '}{comparison.newInterval === 'year' ? t('Yearly') : t('Monthly')}
												</span>
											</div>
										</Col>
									</Row>
								</>
							)}
							<Row className="mb-4">
								{/* Billing Information Section */}
								{isLoadingCustomerData && (
									<>
										<Skeleton className="rounded mt-6" height="30px" count={1} />
										<Row className="my-2">
											<Skeleton className="rounded" height="30px" count={1} />
											<Row>
												{[1].map((_, idx) => (
													<Col className="" lg={4} md={4} xs={4} key={`loading-${idx}`}>
														<Skeleton className="rounded mt-3" height="100px" count={1} />
													</Col>
												))}
											</Row>
										</Row>
									</>
								)}
								{!isLoadingCustomerData && (
									<>
									<h3 className="mb-2">
									{t('Billing information')}
									{/* Validierungsanzeige */}
									{!isLoadingCustomerData && !isBillingInfoComplete && (
										<>
										<span className="badge bg-warning ms-2 text-dark">
											<i className="fe fe-alert-triangle mx-1"></i>
											{t('Please complete')}
										</span>
										</>
									)}
									{!isLoadingCustomerData && isBillingInfoComplete && (
										<>
											<span className="badge bg-success ms-2">
												<i className="fe fe-check mx-1"></i>
												{t('Complete')}
											</span>
										</>
									)}
								</h3><hr></hr>
								<Row className="mb-4">
									<Col lg={12} className="align-items-center ms-3">
										{/* Address */}
										<div className="mb-2 fs-4">
											<h5 className="mt-0">
												{CustomerData.name}
											</h5>
											<p className="text">
														{CustomerData.address?.line1 || <span className="text-warning"><i className="fe fe-alert-triangle mx-1"></i>{t('Street and Number')}</span>}
												{CustomerData.address?.line2 && (
													<>
														<br />
														{CustomerData.address.line2}
													</>
												)}
												<br />
														{CustomerData.address?.postal_code || <span className="text-warning"><i className="fe fe-alert-triangle mx-1"></i>{t('Postal Code')}</span>} {' '}
														{CustomerData.address?.city || <span className="text-warning"><i className="fe fe-alert-triangle mx-1"></i>{t('City')}</span>}
												<br />
														{CustomerData.email || <span className="text-warning"><i className="fe fe-alert-triangle mx-1"></i>{t('Email')}</span>}
											</p>
										</div>
										<div>
											<Button
												variant="link"
												className="text-primary p-0 text-decoration-none"
												onClick={handleShowBillingModal}
											>
												<i className="fe fe-edit-2"></i> {t('Edit')}
											</Button>
										</div>
									</Col>
								</Row>
									</>
							)}
							</Row>
							<Row>
								{/* Notice period */}
								{comparison && comparison.newPrice < comparison.currentPrice && !comparison.currentTrial && (
									<>
										<Col lg={12} md={12} className="mb-3">
											<div className="alert alert-warning">
												{t('You are currently in a paid plan till')} <strong>{comparison.currentBillingPeriod}</strong>{'. '}{t('If you switch now, your current plan will run until the end of the term and automatically switch to the free plan.')}
											</div>
										</Col>
									</>
								)}
							</Row>
							<Row>
								{/* Checkbox */}
								<Col lg={12} md={12} className="mb-3">
									<Form.Check
										type="checkbox"
										id="agbcheckbox"
										onChange={e => setIsButtonDisabled(!(e.target.checked && isBillingInfoComplete))}
									>
										<Form.Check.Input
											type="checkbox"
											required
											onChange={e => setIsButtonDisabled(!(e.target.checked && isBillingInfoComplete))}
										/>
										<Form.Check.Label>
											{t('I accept the')} {' '}
											<Link target='_blank' to="/marketing/about/terms-and-conditions/">
												{t('GTC')}
											</Link>{' '}
											{t('and')} {' '}
											<Link target='_blank' to="/marketing/about/privacy/">
												{t('Privacy Policy')}.{' '}
											</Link>
											{t('Prices are exclusive of value added tax (VAT)')}.
											<span className="text-danger">*</span>
										</Form.Check.Label>
									</Form.Check>
								</Col>
								<Col md={12} sm={12} className="mt-4">
									<Button
										variant="primary"
										disabled={isButtonDisabled}
										onClick={handleAddWithCosts}
									>
										{isLoading ? (
											<>
												<Spinner animation="border" size="sm" className="me-2" />
												{product.amount === 0
													? t('Select free plan')
													: t('Add with costs')}
											</>
										) : (
											<>
												{product.amount === 0
													? t('Select free plan')
													: t('Add with costs')}
											</>
										)}
									</Button>
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</section>
			{/* Modal for Billing Information Form */}
			<Modal
				show={showBillingModal}
				onHide={handleCloseBillingModal}
				size=""
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title>{t('Edit Billing Information')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<BillingInformationForm
						customerData={CustomerData}
						customerTaxData={CustomerTaxData}
						onSubmit={handleBillingInfoUpdate}
						onCancel={handleCloseBillingModal}
						isLoading={isLoadingUpdateCustomerData}
					/>
				</Modal.Body>
			</Modal>
			{/* Create Configration Modal */}
			<ModalCheckout
				show={showCheckoutModal}
				onHide={() => setShowCheckoutModal(false)}
				checkoutSummary=''
				isLoading={isLoadingCheckout}
				showConfetti={showConfetti}
			/>
		</main>
	);
};

export default Checkout;